import { useState, useEffect, useCallback, useRef } from "react";
import { Loader2, MailCheck, Clock, ShieldAlert, QrCode, XCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const OPEN = ["email_offen", "warnung_offen", "wartezeit"];
const when = (iso) => (iso ? new Date(iso).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" }) : "–");
const apiError = (err) => formatApiErrorDetail(err.response?.data?.detail) || err.message;
const line = "flex items-start gap-2 text-sm leading-relaxed";

function Approval({ req, t }) {
  if (req.approved) return <p className={`${line} text-emerald-700`} data-testid="mail-recovery-approved">{t("login.mail_recovery.approved")}</p>;
  if (req.approval_by === "super_admin") return <p className={`${line} text-[#8A5A00]`} data-testid="mail-recovery-approval-pending">{t("login.mail_recovery.approval_super_admin")}</p>;
  return (
    <div className="space-y-2" data-testid="mail-recovery-operator">
      <p className={`${line} text-[#8A5A00]`}>
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        {req.operator_anchor === "fehlt" ? t("login.mail_recovery.operator_missing") : t("login.mail_recovery.approval_operator")}
      </p>
      <p className="break-words rounded-lg border border-[#E6E1F0] bg-white px-3 py-2 text-xs text-zinc-700" data-testid="mail-recovery-operator-step">
        {t("login.mail_recovery.operator_step", { id: req.id })}
      </p>
    </div>
  );
}

function OpenRequest({ req, t }) {
  if (req.status === "email_offen") {
    return (
      <p className={`${line} text-zinc-700`} data-testid="mail-recovery-email-open">
        <MailCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-magenta" aria-hidden="true" />
        {t("login.mail_recovery.email_open", { time: when(req.confirm_expires_at) })}
      </p>
    );
  }
  if (req.status === "warnung_offen") {
    return <p className={`${line} text-[#8A5A00]`} data-testid="mail-recovery-warning-pending">{t("login.mail_recovery.warning_pending")}</p>;
  }
  const waiting = new Date(req.wait_until) > new Date();
  return (
    <div className="space-y-2">
      <p className={`${line} text-zinc-700`} data-testid="mail-recovery-waiting">
        <Clock className="mt-0.5 h-4 w-4 shrink-0 text-brand-magenta" aria-hidden="true" />
        {waiting ? t("login.mail_recovery.waiting", { time: when(req.wait_until) }) : t("login.mail_recovery.waiting_done")}
      </p>
      <Approval req={req} t={t} />
      {req.ready && <p className={`${line} text-emerald-700`} data-testid="mail-recovery-ready">{t("login.mail_recovery.ready")}</p>}
    </div>
  );
}

function Actions({ info, req, busy, run, onSetup, t }) {
  const open = req && OPEN.includes(req.status);
  const btn = "flex h-11 w-full items-center justify-center gap-2 rounded-lg text-sm font-bold disabled:opacity-60";
  if (!open) {
    return (
      <button type="button" disabled={busy || !info.mail_configured} data-testid="mail-recovery-request-button"
        onClick={() => run("request", "mail_handed_over")} className={`${btn} border border-[#E6E1F0] bg-white text-brand-purple hover:bg-secondary`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailCheck className="h-4 w-4" aria-hidden="true" />}{t("login.mail_recovery.request")}
      </button>
    );
  }
  return (
    <div className="space-y-2">
      {req.ready && (
        <button type="button" disabled={busy} data-testid="mail-recovery-complete-button" onClick={() => run("complete", null, onSetup)}
          className={`${btn} bg-brand-magenta text-white hover:bg-brand-magenta-dark`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" aria-hidden="true" />}{t("login.mail_recovery.complete")}
        </button>
      )}
      <button type="button" disabled={busy} data-testid="mail-recovery-cancel-button" onClick={() => run("cancel-own", "cancelled")}
        className="mx-auto flex items-center gap-1.5 text-sm font-semibold text-zinc-600 hover:text-brand-purple">
        <XCircle className="h-4 w-4" aria-hidden="true" />{t("login.mail_recovery.cancel")}
      </button>
    </div>
  );
}

export function EmailRecoveryPanel({ pendingToken, onSetup }) {
  const { t } = useI18n();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const sessionGeneration = useRef(0);
  const call = useCallback(async (path) => (await api.post(`/auth/2fa/email-recovery/${path}`, { pending_token: pendingToken })).data,
    [pendingToken]);

  useEffect(() => {
    const generation = ++sessionGeneration.current;
    setInfo(null); setBusy(false); setError(""); setNotice("");
    call("status")
      .then((d) => { if (generation === sessionGeneration.current) setInfo(d); })
      .catch((err) => { if (generation === sessionGeneration.current) setError(apiError(err)); });
    // Navigation or a replacement pending token invalidates every outstanding response.
    return () => { sessionGeneration.current += 1; };
  }, [call]);

  const run = async (path, noticeKey, done) => {
    const generation = sessionGeneration.current;
    setBusy(true); setError(""); setNotice("");
    try {
      const data = await call(path);
      if (generation !== sessionGeneration.current) return;
      if (done) { done(data); return; }
      setInfo((i) => ({ ...i, request: data.request }));
      if (noticeKey) setNotice(t(`login.mail_recovery.${noticeKey}`));
    } catch (err) { if (generation === sessionGeneration.current) setError(apiError(err)); }
    finally { if (generation === sessionGeneration.current) setBusy(false); }
  };

  if (info && !info.enabled) return null;
  const req = info?.request;
  return (
    <section data-testid="mail-recovery-panel" className="space-y-3 rounded-xl border border-[#E6E1F0] bg-white p-4">
      <h2 className="text-sm font-bold text-brand-purple">{t("login.mail_recovery.title")}</h2>
      {!info && !error && <Loader2 className="h-5 w-5 animate-spin text-brand-magenta" aria-label={t("login.mail_recovery.loading")} data-testid="mail-recovery-loading" />}
      {info && <p className="text-sm leading-relaxed text-zinc-600">{t("login.mail_recovery.intro", { hours: info.wait_hours })}</p>}
      {info && !info.mail_configured && !(req && OPEN.includes(req.status)) && (
        <p role="alert" data-testid="mail-recovery-mail-missing" className="rounded-lg border border-[#F6E3B4] bg-[#FFF6DD] px-3 py-2 text-sm text-[#8A5A00]">
          {t("login.mail_recovery.mail_missing")}
        </p>
      )}
      {req && OPEN.includes(req.status) && <OpenRequest req={req} t={t} />}
      {req && !OPEN.includes(req.status) && (
        <p className="text-sm text-zinc-600" data-testid="mail-recovery-last">{t(`login.mail_recovery.closed.${req.status}`)}</p>
      )}
      {req?.expires_at && OPEN.includes(req.status) && <p className="text-xs text-zinc-500">{t("login.mail_recovery.expires", { time: when(req.expires_at) })}</p>}
      {notice && <p role="status" data-testid="mail-recovery-notice" className="text-sm text-emerald-700">{notice}</p>}
      {error && <p role="alert" data-testid="mail-recovery-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}
      {info && <Actions info={info} req={req} busy={busy} run={run} onSetup={onSetup} t={t} />}
    </section>
  );
}
